import type { PagePagination } from "@/modules/corporate/types/pagePagination"
import type { Page } from "@/modules/corporate/types/pages"
import type { SchoolFilters, SchoolPublicData } from "@/modules/corporate/types/schools"
import type { NetworkSection } from "@/modules/corporate/types/sections"
import type { SimpleSelectOptions } from "@/modules/uiKitOverrides/SelectFilter/Select"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import { usePathname, useSearchParams } from "next/navigation"

import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"
import { Error } from "@jobteaser/ui-kit/components/Error/Error"
import { Layout } from "@jobteaser/ui-kit/components/Layout/Layout"
import { Text, TEXT_COLORS, TEXT_TYPES } from "@jobteaser/ui-kit/components/Text/Text"

import { InternalLink } from "@/modules/corporate/components/InternalLink"
import { NetworkCard } from "@/modules/corporate/components/Sections/Network/NetworkCard"
import { Title } from "@/modules/corporate/components/Sections/Title/Title"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { Link } from "@/modules/routing/components/Link"
import { useHref } from "@/modules/routing/hooks/useHref"
import { useRouter } from "@/modules/routing/hooks/useRouter"
import { getAssetsBaseUrl } from "@/modules/staticAssets/assetsManager/assetsManager"
import { FilterBar } from "@/modules/uiKitOverrides/FilterBar/FilterBar"
import { Pagination } from "@/modules/uiKitOverrides/Pagination/Pagination"
import { Filter } from "@/modules/uiKitOverrides/SelectFilter/Filter"

import styles from "./Network.module.css"

export type NetworkPropsType = {
  page: Page
  pagePagination: PagePagination<SchoolPublicData, SchoolFilters> | null
  section: NetworkSection
}

type FiltersNames = "area_key" | "country_key" | "type_key"
type FilterValue = SimpleSelectOptions[] | SimpleSelectOptions

function updateUrlWithQuery(url: string, query: Record<string, string[]>): string {
  const [path, search] = url.split("?")
  const searchParams = new URLSearchParams(search || "")

  searchParams.delete("page")

  Object.entries(query).forEach(([key, value]) => {
    if (value.length > 0) {
      searchParams.set(key, value.toString())
    } else {
      searchParams.delete(key)
    }
  })

  const searchParamsStringified = searchParams.toString()

  return `${path}${searchParamsStringified ? `?${searchParamsStringified}` : ""}`
}

function createQueryFromFilterValue(
  query: FiltersNames,
  data: FilterValue,
  dependentFilterToCleanOnChange?: FiltersNames
): Partial<Record<FiltersNames, string[]>> {
  const dependentFilter: Partial<Record<FiltersNames, string[]>> = dependentFilterToCleanOnChange
    ? { [dependentFilterToCleanOnChange]: [] }
    : {}

  if (Array.isArray(data)) {
    return { [query]: data.map(({ value }) => value), ...dependentFilter }
  }

  if (data?.value) {
    return { [query]: data.value, ...dependentFilter }
  }

  return { [query]: [], ...dependentFilter }
}

function createFilterValuesFromQuery(query: FiltersNames, currentQuerySearch: URLSearchParams): SimpleSelectOptions[] {
  const currentSearchParam = currentQuerySearch.get(query)

  if (currentSearchParam) {
    return currentSearchParam
      .split(",")
      .reduce((acc: SimpleSelectOptions[], item: string) => [...acc, { label: item, value: item }], [])
  }

  return []
}

export const Network: FunctionComponent<NetworkPropsType> = ({ page, pagePagination, section: { title, useType } }) => {
  const { push } = useRouter()
  const pathname = usePathname()
  const href = useHref()
  const { t } = useTranslation(["corporate_schools_network"])
  const searchParams = useSearchParams()
  const hasItems = !!pagePagination?.items.length
  const gridStyles = clsx(styles.grid, !hasItems && styles.grid__withError)

  return (
    <Layout hasBottomPadding hasTopPadding>
      <Layout.Column>
        {!!title && <Title className={styles.title} isCentered tag="h2" title={title} />}

        {!!pagePagination && (
          <div className={styles.container}>
            {!!pagePagination.filters && (
              <FilterBar className={styles.filters}>
                <Filter
                  id="filter-country"
                  isMulti={false}
                  onChange={(data: FilterValue) => {
                    push(updateUrlWithQuery(href, createQueryFromFilterValue("country_key", data, "area_key")))
                  }}
                  options={pagePagination.filters.country_key.map(({ key }) => ({
                    label: t(`corporate_schools_network.filters.country.${key}`),
                    value: key,
                  }))}
                  placeholder={t("corporate_schools_network.filters.country.placeholder")}
                  value={pagePagination.filters.country_key
                    .filter(({ key }) => key === searchParams.get("country_key"))
                    .map(({ key }) => ({
                      label: t(`corporate_schools_network.filters.country.${key}`),
                      value: key,
                    }))}
                />
                <Filter
                  id="filter-area"
                  isDisabled={!pagePagination.filters.area_key.length && !searchParams.has("area_key")}
                  isMulti
                  onApply={data => {
                    push(updateUrlWithQuery(href, createQueryFromFilterValue("area_key", data)))
                  }}
                  options={pagePagination.filters.area_key.map(({ key, value }) => ({
                    label: value,
                    value: key,
                  }))}
                  placeholder={t("corporate_schools_network.filters.area.placeholder")}
                  value={createFilterValuesFromQuery("area_key", searchParams)}
                />
                {useType.includes("yes") && (
                  <Filter
                    id="filter-type"
                    isMulti
                    onApply={data => {
                      push(updateUrlWithQuery(href, createQueryFromFilterValue("type_key", data)))
                    }}
                    options={pagePagination.filters.type_key.map(({ key }) => ({
                      label: t(`corporate_schools_network.filters.type.${key}`),
                      value: key,
                    }))}
                    placeholder={t("corporate_schools_network.filters.type.placeholder")}
                    value={createFilterValuesFromQuery("type_key", searchParams)}
                  />
                )}
              </FilterBar>
            )}

            {hasItems && (
              <Text color={TEXT_COLORS.GREY} type={TEXT_TYPES.SUBHEAD}>
                {t(`corporate_schools_network.count.title`, { count: pagePagination.totalItems })}
              </Text>
            )}

            <div className={gridStyles}>
              {pagePagination.items.map(item => (
                <NetworkCard
                  country={t(`corporate_schools_network.filters.country.${item.country_key}`)}
                  key={item.id}
                  logoSrc={item.logo && `${getAssetsBaseUrl()}/${item.logo}`}
                  schoolType={t(`corporate_schools_network.filters.type.${item.type_key}`)}
                  title={item.name}
                  useType={useType.includes("yes")}
                />
              ))}

              {!hasItems && (
                <Error
                  action={
                    <Button
                      className={styles.button}
                      component={InternalLink}
                      customTitle={t("corporate_schools_network.error.cta")}
                      page={page}
                      variant={BUTTON_VARIANT.GHOST}
                    />
                  }
                  title={t("corporate_schools_network.error.title")}
                />
              )}
            </div>
            <Pagination
              itemPerPage={pagePagination.itemsPerPage}
              linkComponent={Link}
              linkParam="href"
              linkPathname={pathname}
              linkSearchParams={searchParams.toString()}
              totalCount={pagePagination.totalItems}
            />
          </div>
        )}
      </Layout.Column>
    </Layout>
  )
}
